import { useState, useCallback, useEffect, useRef } from "react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import { useCasesData } from "../data/useCases.jsx";

// Temporary kill-switch: prevent auto-opening Cogi when this section enters view.
const ENABLE_AUTO_COGI_PROMO = false;

function RedesignedUseCases() {
    const sectionRef = useScrollAnimation();
    const [expandedCard, setExpandedCard] = useState(null);
    const [panelExpanded, setPanelExpanded] = useState(false);
    const promoTriggered = useRef(false);
    const promoRef = useRef(null);

    useEffect(() => {
        if (sessionStorage.getItem('eie-promo-shown')) {
            promoTriggered.current = true;
        }
    }, []);

    useEffect(() => {
        if (!ENABLE_AUTO_COGI_PROMO) return;

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting && !promoTriggered.current) {
                promoTriggered.current = true;
                sessionStorage.setItem('eie-promo-shown', '1');
                const cogi = window.Cogi?.instance;
                if (cogi) {
                    document.body.style.overflow = 'hidden';
                    cogi.open();
                    const promoText = "Originally designed for Media & Sport, EIE has proven transformative across industries — from Human Services and Insurance to VR, Training, and Marketing.\n\nThis versatility comes from a fundamental truth: 90% of human decisions at the moment of intent are driven by emotion, not logic.\n\nWatch the demos below and ask me anything — I'll show you how EIE turns that insight into your competitive advantage.";
                    setTimeout(async () => {
                        cogi.addMessage('assistant', promoText);
                        const lastIndex = cogi.messages.length - 1;
                        let ttsSpoke = false;
                        if (lastIndex >= 0) {
                            await cogi.speakMessage(lastIndex);
                            // Poll catches browser TTS fallback
                            await new Promise(resolve => {
                                const check = setInterval(() => {
                                    if (!window.speechSynthesis?.speaking) {
                                        clearInterval(check);
                                        resolve();
                                    }
                                }, 300);
                            });
                            ttsSpoke = true;
                        }
                        if (ttsSpoke) {
                            cogi.close();
                            document.body.style.overflow = '';
                            return;
                        }
                    }, 500);
                    // Fallback: if TTS didn't fire, unlock scroll when user closes manually
                    const poll = setInterval(() => {
                        if (!cogi.isOpen) {
                            clearInterval(poll);
                            document.body.style.overflow = '';
                        }
                    }, 300);
                }
            }
        }, { threshold: 0.3 });

        const el = promoRef.current;
        if (el) observer.observe(el);
        return () => { if (el) observer.unobserve(el); };
    }, []);

    const toggleCard = (id) => {
        setExpandedCard(expandedCard === id ? null : id);
        setPanelExpanded(false);
    };

    return (
        <section ref={(el) => { sectionRef.current = el; promoRef.current = el; }} className="redesign-use-cases">
            <div className="redesign-use-cases-container">
                <div className="redesign-section-header scroll-animate-fade">
                    <div className="redesign-section-label">REAL-WORLD APPLICATIONS</div>
                    <h2 className="redesign-section-title"><span style={{color: '#F50000'}}>E</span>IE in Action</h2>
                    <p className="redesign-section-description">
                        From healthcare to insurance, EIE is transforming industries with real-time emotional intelligence
                    </p>
                </div>
                <div className="redesign-use-cases-grid">
                    {useCasesData.map((useCase) => (
                        <div 
                            key={useCase.id} 
                            className={`redesign-use-case-card ${expandedCard === useCase.id ? 'expanded' : ''} ${useCase.featured ? 'featured' : ''}`}
                        >
                            <div className="redesign-use-case-icon">{useCase.icon}</div>
                            <h3 className="redesign-use-case-title">{useCase.title}</h3>
                            <p className="redesign-use-case-description">{useCase.description}</p>
                            <ul className="redesign-use-case-features">
                                {useCase.features.map((feature, i) => (
                                    <li key={i}>{feature}</li>
                                ))}
                            </ul>
                            <button className="use-case-expand-btn" onClick={(e) => { e.stopPropagation(); toggleCard(useCase.id); }}>
                                Watch Demo
                            </button>
                            
                            {expandedCard === useCase.id && (
                                <>
                                    <div className="use-case-overlay" onClick={() => toggleCard(null)}>
                                        <button className="use-case-close-btn" onClick={() => toggleCard(null)}>×</button>
                                        <div className="use-case-video-background" onClick={(e) => e.stopPropagation()}>
                                            <video
                                                ref={(el) => {
                                                    if (el) {
                                                        el.muted = false;
                                                        el.play().catch(() => {
                                                            el.muted = true;
                                                            el.play();
                                                        });
                                                    }
                                                }}
                                                src={useCase.videoUrl}
                                                controls
                                                playsInline
                                            />
                                        </div>
                                        <div className={`use-case-content-overlay ${panelExpanded ? 'expanded' : ''}`}>
                                            <button 
                                                className="use-case-panel-toggle"
                                                onClick={(e) => { e.stopPropagation(); setPanelExpanded(!panelExpanded); }}
                                            >
                                                <span style={{
                                                    display: 'inline-block',
                                                    transition: 'transform 0.2s ease',
                                                    transform: panelExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                                                }}>
                                                    ▲
                                                </span>
                                            </button>
                                            <div className="use-case-content">
                                                <h3>{useCase.title}</h3>
                                                <p>{useCase.description}</p>
                                                <ul>
                                                    {useCase.features.map((feature, i) => (
                                                        <li key={i}>{feature}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

export default RedesignedUseCases;
